import type { PregnancyStickerId } from "@listou/shared/stickers";

/**
 * The baby week by week, in SA (weeks since the last period, as French follow-up counts).
 * Sizes are the usual averages: crown to rump until 19 SA, head to heel from 20 SA, which
 * is why the length jumps there. Every baby differs; the page says so.
 *
 * The comparison changes with the measure. Until 19 SA the baby is compared by size (a
 * raspberry is as long as it is). From 20 SA it is compared by weight: head to heel, no
 * round fruit matches a 40 cm baby, and "a coconut" at 1.3 kg only reads right by weight.
 * Each produce is picked for a typical weight within about 10% of the baby's.
 */

/** From this week on, the comparison is by weight. */
export const WEIGHT_FROM_SA = 20;

export type BabyWeek = {
  sa: number;
  sticker: PregnancyStickerId;
  /** "une framboise": reads after "la taille d'" or "autant qu'". */
  fruit: string;
  /** Centimetres, one decimal at most. */
  lengthCm: number;
  /** Grams; null while under a gram. */
  weightG: number | null;
  note: string;
};

const week = (
  sa: number,
  sticker: PregnancyStickerId,
  fruit: string,
  lengthCm: number,
  weightG: number | null,
  note: string,
): BabyWeek => ({ sa, sticker, fruit, lengthCm, weightG, note });

export const BABY_WEEKS: BabyWeek[] = [
  week(
    5,
    "sesame",
    "une graine de sésame",
    0.2,
    null,
    "Le tube neural, futur cerveau et moelle épinière, se forme, et l'ébauche du cœur aussi.",
  ),
  week(
    6,
    "lentille",
    "une lentille",
    0.4,
    null,
    "Le cœur commence à battre : l'échographie peut déjà le montrer. Les bourgeons des bras et des jambes apparaissent.",
  ),
  week(
    7,
    "myrtille",
    "une myrtille",
    1,
    null,
    "Le visage se dessine : l'emplacement des yeux et des narines, et les membres s'allongent.",
  ),
  week(
    8,
    "framboise",
    "une framboise",
    1.6,
    1,
    "Les doigts apparaissent, encore reliés. Il bouge déjà, bien trop peu pour que vous le sentiez.",
  ),
  week(
    9,
    "cerise",
    "une cerise",
    2.3,
    2,
    "Les paupières se forment et recouvrent les yeux ; les orteils se séparent.",
  ),
  week(
    10,
    "fraise",
    "une fraise",
    3.1,
    4,
    "L'embryon devient un fœtus : tous ses organes sont en place, il ne leur reste qu'à grandir et mûrir.",
  ),
  week(
    11,
    "figue",
    "une figue",
    4.1,
    7,
    "Les ongles commencent à pousser et il sait ouvrir et fermer les mains.",
  ),
  week(
    12,
    "citron-vert",
    "un citron vert",
    5.4,
    14,
    "C'est la période de la première échographie, qui fixe le début de la grossesse.",
  ),
  week(
    13,
    "citron",
    "un citron",
    7.4,
    23,
    "Ses cordes vocales se forment et ses intestins prennent leur place dans l'abdomen.",
  ),
  week(
    14,
    "peche",
    "une pêche",
    8.7,
    43,
    "Il fait des grimaces. Un fin duvet, le lanugo, commence à couvrir sa peau.",
  ),
  week(
    15,
    "pomme",
    "une pomme",
    10.1,
    70,
    "Ses os se solidifient et il bouge beaucoup, en tous sens.",
  ),
  week(
    16,
    "avocat",
    "un avocat",
    11.6,
    100,
    "Certaines mamans sentent les premiers mouvements, souvent plus tôt quand ce n'est pas le premier bébé.",
  ),
  week(17, "poire", "une poire", 13, 140, "Ses empreintes digitales se dessinent, uniques."),
  week(
    18,
    "poivron",
    "un poivron",
    14.2,
    190,
    "Son oreille interne se développe : il commence à percevoir les sons.",
  ),
  week(
    19,
    "mangue",
    "une mangue",
    15.3,
    240,
    "Un enduit blanc, le vernix, se dépose sur sa peau pour la protéger.",
  ),
  week(
    20,
    "aubergine",
    "une aubergine",
    25.6,
    300,
    "La moitié du chemin. Ses mouvements deviennent nets, et l'échographie du 2e trimestre approche.",
  ),
  week(
    21,
    "pamplemousse",
    "un pamplemousse",
    26.7,
    360,
    "Il avale du liquide amniotique, qui entraîne son système digestif.",
  ),
  week(
    22,
    "raisin",
    "une grosse grappe de raisin",
    27.8,
    430,
    "Ses sourcils et ses premiers cheveux apparaissent.",
  ),
  week(
    23,
    "papaye",
    "une papaye",
    28.9,
    500,
    "Il alterne phases d'éveil et de sommeil, pas forcément aux mêmes heures que vous.",
  ),
  week(
    24,
    "rhubarbe",
    "une botte de rhubarbe",
    30,
    600,
    "Il réagit aux bruits forts. Sa peau, fine, reste plissée.",
  ),
  week(
    25,
    "noix-de-coco",
    "une noix de coco",
    34.6,
    660,
    "Ses mains sont bien formées : il attrape son cordon, touche son visage.",
  ),
  week(26, "blettes", "une botte de blettes", 35.6, 760, "Ses paupières commencent à s'ouvrir."),
  week(
    27,
    "chou-fleur",
    "un chou-fleur",
    36.6,
    875,
    "Il a parfois le hoquet, que vous sentez comme de petits sursauts réguliers.",
  ),
  week(
    28,
    "chou",
    "un chou",
    37.6,
    1000,
    "Début du 3e trimestre. Il ouvre et ferme les yeux et perçoit la lumière.",
  ),
  week(
    29,
    "butternut",
    "une courge butternut",
    38.6,
    1150,
    "Il prend du poids vite ; ses muscles et ses poumons continuent de mûrir.",
  ),
  week(
    30,
    "potimarron",
    "un potimarron",
    39.9,
    1300,
    "Il accumule de la graisse sous la peau, qui va l'aider à garder sa chaleur.",
  ),
  week(
    31,
    "ananas",
    "un ananas",
    41.1,
    1500,
    "Il tourne la tête d'un côté à l'autre et ses mouvements sont plus puissants.",
  ),
  week(
    32,
    "chou-milan",
    "un gros chou de Milan",
    42.4,
    1700,
    "C'est la période de la 3e échographie. Ses ongles atteignent le bout des doigts.",
  ),
  week(
    33,
    "regime-bananes",
    "un régime de bananes",
    43.7,
    1900,
    "Son cerveau grandit très vite ; les os de son crâne restent souples pour la naissance.",
  ),
  week(
    34,
    "courge-spaghetti",
    "une courge spaghetti",
    45,
    2150,
    "Le lanugo commence à disparaître.",
  ),
  week(
    35,
    "melon-vert",
    "un gros melon vert",
    46.2,
    2400,
    "La place manque : il bouge moins amplement, mais il bouge toujours.",
  ),
  week(
    36,
    "pommes-de-terre",
    "un sac de pommes de terre de 2,5 kg",
    47.4,
    2600,
    "La plupart des bébés se placent tête en bas pour la naissance.",
  ),
  week(
    37,
    "oranges",
    "un filet d'oranges de 3 kg",
    48.6,
    2850,
    "Il est désormais à terme : ses poumons sont presque prêts à respirer.",
  ),
  week(
    38,
    "pasteque",
    "une petite pastèque",
    49.8,
    3100,
    "Il continue de prendre du poids, environ 200 g par semaine.",
  ),
  week(39, "jacquier", "un jacquier", 50.7, 3300, "Il s'entraîne à respirer, avaler et téter."),
  week(
    40,
    "citrouille",
    "une citrouille",
    51.2,
    3450,
    "Le terme retenu par la plupart des pays. Plus que quelques jours, ou semaines.",
  ),
  week(
    41,
    "potiron",
    "un gros potiron",
    51.7,
    3600,
    "Le terme retenu en France. Votre sage-femme surveille de près la fin de grossesse.",
  ),
];

/** "a la taille d'une framboise" or "pèse autant qu'un potimarron". */
export const comparison = (entry: BabyWeek) =>
  entry.sa < WEIGHT_FROM_SA ? `a la taille d'${entry.fruit}` : `pèse autant qu'${entry.fruit}`;

/** The gallery's shorter form: "La taille d'une framboise", "Le poids d'un potimarron". */
export const comparisonLabel = (entry: BabyWeek) =>
  entry.sa < WEIGHT_FROM_SA ? `La taille d'${entry.fruit}` : `Le poids d'${entry.fruit}`;

/** The week's entry, clamped to the table's range; null before 5 SA. */
export const babyWeek = (sa: number): BabyWeek | null => {
  if (sa < 5) return null;
  return BABY_WEEKS.find((entry) => entry.sa === Math.min(sa, 41)) ?? null;
};

export const formatWeight = (grams: number | null) =>
  grams === null
    ? "moins d'1 g"
    : grams >= 1000
      ? `${(grams / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} kg`
      : `${grams} g`;

export const formatLength = (cm: number) =>
  cm < 1 ? `${Math.round(cm * 10)} mm` : `${cm.toLocaleString("fr-FR")} cm`;
